import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const root = window.Shopify?.routes?.root || '/';
const activeUpdates = new Set();
let protectionBusy = false;
const moneyTimerKey = 'grohour-cart-deadline-v1';
let memoryDeadline = 0;

function deadlineFor(duration) {
  let deadline = memoryDeadline;
  try {
    deadline = Number(sessionStorage.getItem(moneyTimerKey)) || deadline;
  } catch {}

  if (!deadline) {
    deadline = Date.now() + duration * 1000;
    try {
      sessionStorage.setItem(moneyTimerKey, String(deadline));
    } catch {}
  }

  memoryDeadline = deadline;
  return deadline;
}

class GrohourCartBanner extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('theme-drawer');
    this.onOpen = () => this.start();
    this.drawer?.addEventListener(DrawerOpenEvent.eventName, this.onOpen);

    const duration = Math.max(60, Number(this.dataset.duration) || 240);
    const digits = this.querySelector('[data-gh-timer]');

    if (digits) {
      const deadline = deadlineFor(duration);
      const now = Date.now();
      const repeatSeconds = 90;

      const remaining = now < deadline
        ? Math.ceil((deadline - now) / 1000)
        : repeatSeconds - (
            Math.floor((now - deadline) / 1000) % repeatSeconds
          );

      digits.textContent = this.format(remaining);
      document.documentElement.style.setProperty(
        '--gh-cart-timer-display', JSON.stringify(this.format(remaining))
      );
    }

    if (this.drawer?.isOpen || this.drawer?.hasAttribute('open')) {
      this.start();
    }
  }

  disconnectedCallback() {
    clearInterval(this.interval);
    this.drawer?.removeEventListener(DrawerOpenEvent.eventName, this.onOpen);
  }

  format(seconds) {
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }

  start() {
    clearInterval(this.interval);

    const digits = this.querySelector('[data-gh-timer]');
    if (!digits) return;

    const deadline = deadlineFor(
      Math.max(60, Number(this.dataset.duration) || 240)
    );

    const tick = () => {
      const now = Date.now();
      const repeatSeconds = 90;

      const remaining = now < deadline
        ? Math.ceil((deadline - now) / 1000)
        : repeatSeconds - (
            Math.floor((now - deadline) / 1000) % repeatSeconds
          );

      digits.textContent = this.format(remaining);
      document.documentElement.style.setProperty(
        '--gh-cart-timer-display', JSON.stringify(this.format(remaining))
      );
      this.querySelector('[data-gh-timer-strip]')?.removeAttribute('hidden');
    };

    tick();
    this.interval = setInterval(tick, 1000);
  }
}

async function readJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...options
  });

  const data = await response.json();

  if (!response.ok || data.errors || data.status >= 400) {
    const errorText = typeof data.errors === 'string'
      ? data.errors
      : data.description || data.message;

    throw new Error(
      errorText || 'Could not update your cart. Please try again.'
    );
  }

  return data;
}

function currentDrawer() {
  return document.querySelector('#cart-drawer .gh-cart');
}

function setBusy(busy) {
  protectionBusy = busy;

  const drawer = currentDrawer();
  if (!drawer) return;

  drawer.toggleAttribute('data-gh-busy', busy);

  if (busy) {
    drawer.setAttribute('aria-busy', 'true');
  } else {
    drawer.removeAttribute('aria-busy');
  }

  drawer.querySelectorAll('[data-gh-protection-toggle]').forEach(button => {
    button.disabled = busy;
  });
}

function showError(message) {
  const drawer = currentDrawer();
  let error = drawer?.querySelector('[data-gh-protection-error]');

  if (!error && drawer) {
    error = document.createElement('p');
    error.className = 'gh-cart-error';
    error.setAttribute('role', 'alert');
    drawer.querySelector('.cart-drawer__summary')?.prepend(error);
  }

  if (error) {
    error.textContent = message;
    error.hidden = false;
  }
}

async function waitForCart() {
  await new Promise(resolve => setTimeout(resolve, 350));

  for (let pass = 0; pass < 5; pass++) {
    const nativePending =
      customElements.get('cart-items-component')?.pendingQuantityUpdates || [];

    const pending = [...activeUpdates, ...nativePending];

    if (!pending.length) break;
    await Promise.allSettled(pending);
  }
}

async function changeProtection(productId, variantId, orphanOnly = false) {
  if (protectionBusy) return;

  const button = currentDrawer()?.querySelector('[data-gh-protection-toggle]');
  const previousChecked = button?.getAttribute('aria-checked') === 'true';

  // Move immediately on a customer tap, before any network request.
  if (button && !orphanOnly) {
    button.setAttribute('aria-checked', previousChecked ? 'false' : 'true');
  }

  setBusy(true);

  let deferred;
  let dispatched = false;
  let changed = false;
  let confirmedChecked = previousChecked;

  try {
    await waitForCart();

    const cart = await readJson(`${root}cart.js`);

    const protection = cart.items.filter(
      item => String(item.product_id) === String(productId)
    );

    const goods = cart.items.filter(
      item => String(item.product_id) !== String(productId)
    );

    confirmedChecked = protection.length > 0;

    if (orphanOnly && (goods.length || !protection.length)) return;
    if (!protection.length && !goods.length) return;

    const removing = protection.length > 0;
    if (!removing && !variantId) return;

    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map(el => el.dataset.sectionId)
      )
    ];

    if (!sectionIds.includes('cart-drawer-section')) {
      sectionIds.push('cart-drawer-section');
    }

    const selectedSections = sectionIds.slice(0, 5);

    if (typeof sectionRenderer.abortRender === 'function') {
      selectedSections.forEach(id => sectionRenderer.abortRender(id));
    }

    const payload = {
      sections: selectedSections.join(','),
      sections_url: window.location.pathname
    };

    const lines = removing
      ? protection.map(item => ({ id: item.key, quantity: 0 }))
      : [{ merchandiseId: String(variantId), quantity: 1 }];

    if (removing) {
      payload.updates = Object.fromEntries(
        protection.map(item => [item.key, 0])
      );
    } else {
      payload.items = [{ id: Number(variantId), quantity: 1 }];
    }

    deferred = CartLinesUpdateEvent.createPromise();

    const source =
      document.querySelector('grohour-cart-protection') ||
      document.querySelector('grohour-cart-banner') ||
      document;

    source.dispatchEvent(
      new CartLinesUpdateEvent({
        action: removing ? 'remove' : 'add',
        context: 'cart',
        lines,
        promise: deferred.promise
      })
    );

    dispatched = true;

    const response = await readJson(
      `${root}cart/${removing ? 'update' : 'add'}.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );

    changed = true;
    confirmedChecked = !removing;

    const updatedCart = removing
      ? response
      : await readJson(`${root}cart.js`);

    confirmedChecked = updatedCart.items.some(
      item => String(item.product_id) === String(productId)
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updatedCart),
      detail: {
        sections: response.sections || undefined,
        items: updatedCart.items,
        itemCount: updatedCart.item_count,
        source: 'grohour-cart-protection',
        didError: false
      }
    });

    await new Promise(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(resolve))
    );

    document.querySelector('[data-gh-protection-toggle]')
      ?.focus({ preventScroll: true });
  } catch (error) {
    if (dispatched) {
      deferred?.reject(error);

      document.dispatchEvent(
        new CartErrorEvent({
          error: error.message,
          code: 'SERVICE_UNAVAILABLE'
        })
      );
    }

    showError(
      changed
        ? 'Your cart changed, but could not refresh. Reload this page before continuing.'
        : error.message || 'Could not update your cart. Please try again.'
    );
  } finally {
    // Reconcile the switch with the confirmed state, including failures.
    currentDrawer()?.querySelectorAll('[data-gh-protection-toggle]')
      .forEach(toggle => {
        toggle.setAttribute('aria-checked', String(confirmedChecked));
      });

    setBusy(false);
  }
}

class GrohourCartProtection extends HTMLElement {
  connectedCallback() {
    this.onClick = event => {
      if (!event.target.closest('[data-gh-protection-toggle]')) return;

      event.preventDefault();

      const error = this.querySelector('[data-gh-protection-error]');
      if (error) error.hidden = true;

      changeProtection(this.dataset.productId, this.dataset.variantId);
    };

    this.addEventListener('click', this.onClick);

    this.querySelector('[data-gh-protection-toggle]')
      ?.toggleAttribute('disabled', protectionBusy);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }
}

document.addEventListener('click', event => {
  if (!protectionBusy || !(event.target instanceof Element)) return;

  if (
    event.target.closest(
      '.gh-cart .cart-items__quantity-controls, .gh-cart .cart__ctas, .gh-cart [data-gh-protection-toggle]'
    )
  ) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

document.addEventListener('submit', event => {
  if (
    protectionBusy &&
    event.target instanceof Element &&
    event.target.closest('.gh-cart')
  ) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (!event.promise) return;

  activeUpdates.add(event.promise);

  event.promise.then(({ detail }) => {
    const banner = document.querySelector('grohour-cart-banner');
    const productId = banner?.dataset.productId;
    const items = detail?.items;

    if (
      productId &&
      items?.length &&
      items.every(item => String(item.product_id) === productId)
    ) {
      setTimeout(() => changeProtection(productId, null, true), 450);
    }
  }).catch(() => {}).finally(() => {
    activeUpdates.delete(event.promise);
  });
});

if (!customElements.get('grohour-cart-banner')) {
  customElements.define('grohour-cart-banner', GrohourCartBanner);
}

if (!customElements.get('grohour-cart-protection')) {
  customElements.define('grohour-cart-protection', GrohourCartProtection);
}